import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Row,
  Col,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label,
  FormGroup,
} from "reactstrap";
import axios from "axios";
import { API_BASE_URL } from "config.js";
import Swal from "sweetalert2";
import { Spinner } from "reactstrap";
import { Navigate } from "react-router-dom";

const Users = () => {
  const storedUser = localStorage.getItem("user");
  const currentUser = storedUser ? JSON.parse(storedUser) : null;
  const isMasterAdmin = currentUser && currentUser.type === "admin";

  const [isLoading, setIsLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [statusFilter, setStatusFilter] = useState("active");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState("add");
  const [user, setUser] = useState({});
  const [error, setError] = useState("");
  const [unassignedNumbers, setUnassignedNumbers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Vetting Review & Twilio States
  const [vettingModalOpen, setVettingModalOpen] = useState(false);
  const [selectedVettingUser, setSelectedVettingUser] = useState(null);
  const [submittingVetting, setSubmittingVetting] = useState(false);
  const [syncingA2P, setSyncingA2P] = useState(false);

  const [removeModal, setRemoveModal] = useState({
    isOpen: false,
    userToDelete: null,
    userObj: null,
    deleting: false
  });

  const getA2PStatusBadge = (status) => {
    const st = (status || "unsubmitted").toLowerCase();
    let bg = "#e2e3e5", color = "#383d41", label = "Unregistered";
    if (st === "approved") { bg = "#d4edda"; color = "#155724"; label = "Approved"; }
    else if (st === "pending" || st === "in_progress") { bg = "#fff3cd"; color = "#856404"; label = "Pending Review"; }
    else if (st === "failed" || st === "rejected") { bg = "#f8d7da"; color = "#721c24"; label = "Failed / Rejected"; }
    return (
      <span
        className="badge px-2 py-1 font-weight-bold"
        style={{
          backgroundColor: bg,
          color: color,
          borderRadius: "4px",
          textTransform: "uppercase",
          fontSize: "0.75rem",
        }}
      >
        {label}
      </span>
    );
  };

  const toggleModal = () => {
    setModalOpen(!modalOpen);
    setError("");
  };

  const openAddModal = async () => {
    setModalType("add");
    setUser({});

    await fetchUnassignedNumbers();
    toggleModal();
  };

  const openEditModal = async (user) => {
    try {
      const assigned = await fetchAssignedNumbers(user._id);
      const unassignedRes = await axios.get(
        `${API_BASE_URL}/users/unassigned-numbers`,
        { headers: getAuthHeaders() }
      );

      const unassigned = unassignedRes.data.success
        ? unassignedRes.data.data
        : [];

      const combined = [
        ...unassigned,
        ...assigned.filter(
          (num) => !unassigned.find((un) => un._id === num._id)
        ),
      ];

      setUnassignedNumbers(combined);

      setUser({
        ...user,
        phoneNumbers: assigned.map((n) => n._id),
      });

      setModalType("edit");
      toggleModal();
    } catch (err) {
      console.error("Error in openEditModal:", err);
    }
  };

  const handleUserChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === "checkbox" ? checked : value;
    setUser({ ...user, [name]: val });
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handleAddUser = async () => {
    setLoading(true);

    try {
      const {
        name,
        email,
        password,
        confirmPassword,
        smsCost,
        phoneNumbers,
        type,
      } = user;

      if (!name || !email || !password || !confirmPassword || !smsCost) {
        setError("All fields are required.");
        Swal.fire({
          title: "Error!",
          text: "All fields are required.",
          icon: "error",
          confirmButtonText: "OK",
        });
        return;
      }

      if (password !== confirmPassword) {
        setError("Password and confirm password do not match.");
        Swal.fire({
          title: "Error!",
          text: "Password and confirm password do not match.",
          icon: "error",
          confirmButtonText: "OK",
        });
        return;
      }

      const response = await axios.post(
        `${API_BASE_URL}/users`,
        {
          name,
          email,
          password,
          smsCost,
          type: type || "user",
          phoneNumbers: phoneNumbers || [],
        },
        { headers: getAuthHeaders() }
      );

      if (response.data.success) {
        fetchUsers();
        toggleModal();

        Swal.fire({
          title: "Success!",
          text: "User added successfully.",
          icon: "success",
          confirmButtonText: "OK",
        });
      } else {
        setError(response.data.message);

        Swal.fire({
          title: "Error!",
          text: `${response.data.message}`,
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      setError("An error occurred while adding the user.");
      console.error("Error adding user:", error);
      Swal.fire({
        title: "Error!",
        text: "An error occurred while adding the user.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = async () => {
    setLoading(true);

    try {
      const response = await axios.put(
        `${API_BASE_URL}/users/${user._id}`,
        {
          name: user.name,
          email: user.email,
          smsCost: user.smsCost,
          type: user.type || "user",
          phoneNumbers: user.phoneNumbers || [],
        },
        { headers: getAuthHeaders() }
      );

      if (response.data.success) {
        fetchUsers();
        toggleModal();

        Swal.fire({
          title: "Success!",
          text: "User updated successfully.",
          icon: "success",
          confirmButtonText: "OK",
        });
      } else {
        setError("An error occurred while updating the user.");
        Swal.fire({
          title: "Error!",
          text: `${response.data.message}`,
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      setError("An error occurred while updating the user.");
      console.error("Error updating user:", error);
      Swal.fire({
        title: "Error!",
        text: "An error occurred while updating the user.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  // Twilio & A2P Actions
  const openVettingModal = (user) => {
    setSelectedVettingUser({
      ...user,
      twilioSubaccountSid: user.twilioSubaccountSid || "",
      twilioSubauthToken: user.twilioSubauthToken || "",
      twilioMessagingServiceSid: user.twilioMessagingServiceSid || "",
      twilioBrandSid: user.twilioBrandSid || "",
      twilioA2PCampaignSid: user.twilioA2PCampaignSid || "",
      businessRegistrationStatus: user.businessRegistrationStatus || "unsubmitted",
      campaignRegistrationStatus: user.campaignRegistrationStatus || "unsubmitted",
      forceTestMessage: user.forceTestMessage || false,
    });
    setVettingModalOpen(true);
  };

  const handleVettingFieldChange = (section, field, value) => {
    setSelectedVettingUser(prev => {
      if (!prev) return null;
      return {
        ...prev,
        [field]: value
      };
    });
  };

  const handleAdminSyncA2P = async () => {
    if (!selectedVettingUser) return;
    setSyncingA2P(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/users/admin/sync-a2p-status/${selectedVettingUser._id}`,
        {},
        { headers: getAuthHeaders() }
      );

      if (res.data.success) {
        const updated = {
          ...selectedVettingUser,
          businessRegistrationStatus: res.data.businessRegistrationStatus,
          campaignRegistrationStatus: res.data.campaignRegistrationStatus,
          twilioMessagingServiceSid: res.data.twilioMessagingServiceSid || selectedVettingUser.twilioMessagingServiceSid,
        };
        setSelectedVettingUser(updated);
        setUsers(users.map(u => u._id === selectedVettingUser._id ? { ...u, ...updated } : u));

        Swal.fire({
          icon: "success",
          title: "Status Synced with Twilio!",
          html: `<div style="text-align: left; padding: 10px;">
                  <div><strong>Brand Status:</strong> <span class="badge bg-primary text-white">${res.data.businessRegistrationStatus?.toUpperCase() || "UNSUBMITTED"}</span></div>
                  <div class="mt-2"><strong>Campaign Status:</strong> <span class="badge bg-primary text-white">${res.data.campaignRegistrationStatus?.toUpperCase() || "UNSUBMITTED"}</span></div>
                 </div>`,
          confirmButtonColor: "#014c95",
        });
      }
    } catch (err) {
      console.error("Error syncing status from Twilio:", err);
      Swal.fire("Sync Error", err.response?.data?.message || "Failed to sync status with Twilio.", "error");
    } finally {
      setSyncingA2P(false);
    }
  };

  const handleSaveVettingDetails = async () => {
    if (!selectedVettingUser) return;
    setSubmittingVetting(true);
    try {
      const res = await axios.put(`${API_BASE_URL}/users/${selectedVettingUser._id}`, selectedVettingUser, {
        headers: getAuthHeaders()
      });
      if (res.data.success) {
        Swal.fire("Saved!", "Twilio SIDs, credentials, and carrier settings saved successfully.", "success");
        setUsers(users.map(u => u._id === selectedVettingUser._id ? { ...u, ...selectedVettingUser } : u));
        setVettingModalOpen(false);
      }
    } catch (err) {
      Swal.fire("Error", err.response?.data?.message || "Failed to save configuration.", "error");
    } finally {
      setSubmittingVetting(false);
    }
  };

  const fetchUsers = async (filterOverride) => {
    try {
      setIsLoading(true);
      const filter = filterOverride !== undefined ? filterOverride : statusFilter;
      const response = await axios.get(`${API_BASE_URL}/users?status=${filter}`, {
        headers: getAuthHeaders(),
      });
      setUsers(response.data?.data || []);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error("Error fetching users:", error);
    }
  };

  const fetchUnassignedNumbers = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/users/unassigned-numbers`,
        {
          headers: getAuthHeaders(),
        }
      );

      if (response.data.success) {
        setUnassignedNumbers(response.data.data || []);
      } else {
        console.error("Unassigned number fetch failed:", response.data.message);
      }
    } catch (error) {
      console.error("Error fetching unassigned numbers:", error);
    }
  };

  const fetchAssignedNumbers = async (userId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/users/${userId}/assigned-numbers`,
        {
          headers: getAuthHeaders(),
        }
      );
      return response.data.assignedNumbers || [];
    } catch (error) {
      console.error("Error fetching assigned numbers:", error);
      return [];
    }
  };

  const handleReactivateUser = (targetUser) => {
    Swal.fire({
      title: "Reactivate Tenant",
      html: `Are you sure you want to restore and reactivate <strong>${targetUser.name}</strong> (${targetUser.email})?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#28a745",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Reactivate Tenant",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await axios.put(
            `${API_BASE_URL}/users/reactivate/${targetUser._id}`,
            {},
            { headers: getAuthHeaders() }
          );
          fetchUsers();
          Swal.fire({
            title: "Reactivated!",
            text: res.data?.message || "Tenant account reactivated successfully.",
            icon: "success",
            confirmButtonColor: "#014c95",
          });
        } catch (err) {
          console.error("Error reactivating user:", err);
          Swal.fire({
            title: "Error",
            text: err.response?.data?.message || "Failed to reactivate tenant.",
            icon: "error",
            confirmButtonColor: "#d33",
          });
        }
      }
    });
  };

  const openRemoveModal = (targetUser) => {
    setRemoveModal({
      isOpen: true,
      userToDelete: targetUser._id || targetUser,
      userObj: typeof targetUser === "object" ? targetUser : null,
      deleting: false
    });
  };

  const closeRemoveModal = () => {
    setRemoveModal({ isOpen: false, userToDelete: null, userObj: null, deleting: false });
  };

  const handleInitialDeleteClick = () => {
    // Step 2: Final Confirmation
    Swal.fire({
      title: "Final Confirmation",
      html: `Are you absolutely sure you want to deactivate and remove <strong>${removeModal.userObj?.name || "this tenant"}</strong> (${removeModal.userObj?.email || ""})?<br/><br/><small class="text-muted">This will remove the tenant from your active portal.</small>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Deactivate Tenant",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        executeDeleteUser();
      }
    });
  };

  const executeDeleteUser = async () => {
    try {
      setRemoveModal(prev => ({ ...prev, deleting: true }));
      const res = await axios.delete(
        `${API_BASE_URL}/users/${removeModal.userToDelete}`,
        { headers: getAuthHeaders() }
      );

      closeRemoveModal();
      fetchUsers();

      Swal.fire({
        title: "Deactivated!",
        text: res.data?.message || "Tenant deactivated and removed successfully.",
        icon: "success",
        confirmButtonColor: "#014c95",
        confirmButtonText: "OK",
      });
    } catch (error) {
      console.error("Error deleting user:", error);
      const errorMsg = error.response?.data?.message || "An error occurred while deleting the user.";
      Swal.fire({
        title: "Cannot Deactivate Tenant",
        text: errorMsg,
        icon: "error",
        confirmButtonColor: "#d33",
        confirmButtonText: "OK",
      });
    } finally {
      setRemoveModal(prev => ({ ...prev, deleting: false }));
    }
  };

  useEffect(() => {
    if (isMasterAdmin) {
      fetchUsers();
    }
  }, []);

  if (!isMasterAdmin) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  return (
    <Container fluid className="p-3">
      <Row>
        <Col lg="12">
          <Card>
            <CardHeader tag="h2" className="font-weight-bold">
              Users Management
            </CardHeader>
            <CardBody>
              <div className="d-flex flex-wrap align-items-center justify-content-between mb-3 gap-2">
                <Button
                  color="primary"
                  className="bg-primary"
                  onClick={openAddModal}
                >
                  <i className="fas fa-user-plus me-1"></i> Add User
                </Button>

                <div className="btn-group" role="group" aria-label="User status filter">
                  <button
                    type="button"
                    className={`btn btn-sm ${statusFilter === "active" ? "btn-primary font-weight-bold" : "btn-outline-secondary"}`}
                    onClick={() => {
                      setStatusFilter("active");
                      fetchUsers("active");
                    }}
                  >
                    Active Tenants
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${statusFilter === "deactivated" ? "btn-primary font-weight-bold" : "btn-outline-secondary"}`}
                    onClick={() => {
                      setStatusFilter("deactivated");
                      fetchUsers("deactivated");
                    }}
                  >
                    Deactivated Tenants
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${statusFilter === "all" ? "btn-primary font-weight-bold" : "btn-outline-secondary"}`}
                    onClick={() => {
                      setStatusFilter("all");
                      fetchUsers("all");
                    }}
                  >
                    All Tenants
                  </button>
                </div>
              </div>

              {isLoading ? (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                  <div className="mt-2 text-muted">Loading users...</div>
                </div>
              ) : (
                <Table striped bordered responsive hover>
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>SMS Cost/User</th>
                      <th>A2P Brand</th>
                      <th>A2P Campaign</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.length > 0 ? (
                      users.map((user, index) => (
                        <tr key={user._id} style={{ opacity: user.isDeleted ? 0.75 : 1 }}>
                          <td>{index + 1}</td>
                          <td>
                            {user.name}
                            {user.isDeleted && (
                              <span className="badge bg-secondary text-white ms-2 ml-2" style={{ fontSize: "0.7rem" }}>
                                DEACTIVATED
                              </span>
                            )}
                          </td>
                          <td>{user.email}</td>
                          <td style={{ textTransform: "capitalize" }}>{user.type || "user"}</td>
                          <td>{user.smsCost ? `$${user.smsCost}` : "N/A"}</td>
                          <td>
                            {getA2PStatusBadge(user.businessRegistrationStatus)}
                          </td>
                          <td>
                            {getA2PStatusBadge(user.campaignRegistrationStatus)}
                          </td>
                          <td>
                            {user.isDeleted ? (
                              <Button
                                size="sm"
                                color="success"
                                className="me-2 mr-2 bg-success text-white font-weight-bold"
                                onClick={() => handleReactivateUser(user)}
                              >
                                <i className="fas fa-undo me-1"></i> Reactivate
                              </Button>
                            ) : (
                              <>
                                <Button
                                  size="sm"
                                  className="me-2 mr-2 bg-secondary"
                                  onClick={() => openEditModal(user)}
                                >
                                  Edit
                                </Button>
                                <Button
                                  size="sm"
                                  className="me-2 mr-2 bg-danger"
                                  onClick={() => openRemoveModal(user)}
                                >
                                  Delete
                                </Button>
                              </>
                            )}
                            <Button
                              size="sm"
                              color="info"
                              className="bg-info text-white font-weight-bold"
                              onClick={() => openVettingModal(user)}
                            >
                              Manage Twilio & A2P
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan="8"
                          className="text-center fw-bold table-cell"
                        >
                          No {statusFilter !== "all" ? statusFilter : ""} users found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Add/Edit Modal */}
      <Modal isOpen={modalOpen} toggle={toggleModal}>
        <ModalHeader toggle={toggleModal}>
          {modalType === "add" ? "Add" : "Edit"} User
        </ModalHeader>
        <ModalBody>
          {error && <div className="alert alert-danger">{error}</div>}
          <FormGroup>
            <Label for="name">
              Name <span className="text-danger">*</span>
            </Label>
            <Input
              name="name"
              value={user.name || ""}
              onChange={handleUserChange}
              placeholder="Enter name"
            />
          </FormGroup>
          <FormGroup>
            <Label for="email">
              Email <span className="text-danger">*</span>
            </Label>
            <Input
              name="email"
              value={user.email || ""}
              onChange={handleUserChange}
              placeholder="Enter email"
            />
          </FormGroup>
          {modalType === "add" && (
            <>
              <FormGroup>
                <Label for="password">
                  Password <span className="text-danger">*</span>
                </Label>
                <Input
                  type="password"
                  name="password"
                  value={user.password || ""}
                  onChange={handleUserChange}
                />
              </FormGroup>
              <FormGroup>
                <Label for="confirmPassword">
                  Confirm Password <span className="text-danger">*</span>
                </Label>
                <Input
                  type="password"
                  name="confirmPassword"
                  value={user.confirmPassword || ""}
                  onChange={handleUserChange}
                />
              </FormGroup>
            </>
          )}
          <FormGroup>
            <Label for="smsCost">
              Per SMS Cost <span className="text-danger">*</span>
            </Label>
            <Input
              name="smsCost"
              value={user.smsCost || ""}
              onChange={handleUserChange}
              placeholder="e.g. 0.02"
            />
          </FormGroup>
          <FormGroup>
            <Label for="type">
              User Role <span className="text-danger">*</span>
            </Label>
            <Input
              type="select"
              name="type"
              value={user.type || "user"}
              onChange={handleUserChange}
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </Input>
          </FormGroup>
          <FormGroup className="mt-3 mb-3 p-3 bg-light rounded border">
            <div className="d-flex align-items-center mb-1">
              <input
                type="checkbox"
                id="forceTestMessage"
                name="forceTestMessage"
                checked={user.forceTestMessage || false}
                onChange={handleUserChange}
                style={{
                  width: "18px",
                  height: "18px",
                  cursor: "pointer",
                  marginRight: "10px",
                  accentColor: "#014c95"
                }}
              />
              <label htmlFor="forceTestMessage" className="mb-0 fw-bold" style={{ fontWeight: "700", cursor: "pointer", fontSize: "0.95rem", color: "#1e293b" }}>
                Force Test Message Gate
              </label>
            </div>
            <div className="text-muted small" style={{ marginLeft: "28px", fontSize: "0.85rem", lineHeight: "1.4" }}>
              Enforce that this customer must send a test SMS and obtain administrator approval before launching campaigns.
            </div>
          </FormGroup>
          <FormGroup>
            <Label for="phoneNumbers" className="mt-2">
              Assign Phone Numbers
            </Label>
            <Input
              type="select"
              multiple
              name="phoneNumbers"
              value={user.phoneNumbers || []}
              onChange={(e) =>
                setUser({
                  ...user,
                  phoneNumbers: Array.from(
                    e.target.selectedOptions,
                    (opt) => opt.value
                  ),
                })
              }
            >
              {unassignedNumbers.map((num) => (
                <option key={num._id} value={num._id}>
                  {num.phoneNumber}
                </option>
              ))}
            </Input>
            <small className="text-muted">
              Hold Ctrl (Cmd on Mac) to select multiple
            </small>
          </FormGroup>
        </ModalBody>
        <ModalFooter className="justify-content-end">
          <Button
            color="primary"
            className="bg-primary mr-2"
            onClick={modalType === "add" ? handleAddUser : handleEditUser}
            disabled={loading}
          >
            {loading
              ? modalType === "add"
                ? "Adding..."
                : "Updating..."
              : modalType === "add"
              ? "Add User"
              : "Update User"}
          </Button>
          <Button
            color="secondary"
            className="bg-secondary"
            onClick={toggleModal}
            disabled={loading}
          >
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Modal (Step 1) */}
      <Modal isOpen={removeModal.isOpen} toggle={closeRemoveModal} centered>
        <ModalHeader toggle={closeRemoveModal} className="text-danger fw-bold">
          <i className="fas fa-exclamation-triangle me-2"></i> Deactivate Tenant Account
        </ModalHeader>
        <ModalBody>
          <p className="mb-2">
            Are you sure you want to deactivate and remove this tenant?
          </p>
          <div className="p-3 bg-light rounded border mb-3">
            <div><strong>Name:</strong> {removeModal.userObj?.name || "N/A"}</div>
            <div><strong>Email:</strong> {removeModal.userObj?.email || "N/A"}</div>
            <div><strong>Role:</strong> {removeModal.userObj?.type || "user"}</div>
          </div>
          <div className="alert alert-warning border-0 small mb-0">
            <strong><i className="fas fa-info-circle me-1"></i> Phone Numbers Note:</strong> Ensure all phone numbers assigned to this tenant have been released or reassigned. Deletion will be rejected if active numbers remain to prevent recurring carrier charges.
          </div>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-end">
          <div>
            <Button
              color="secondary"
              onClick={closeRemoveModal}
              className="bg-secondary me-2 mr-2"
              disabled={removeModal.deleting}
            >
              Cancel
            </Button>
            <Button
              color="danger"
              onClick={handleInitialDeleteClick}
              className="bg-danger font-weight-bold"
              disabled={removeModal.deleting}
            >
              {removeModal.deleting ? "Deactivating..." : "Proceed to Deactivate"}
            </Button>
          </div>
        </ModalFooter>
      </Modal>

      {/* Manage Twilio & A2P 10DLC Modal */}
      <Modal isOpen={vettingModalOpen} toggle={() => setVettingModalOpen(false)} size="lg" centered>
        <ModalHeader toggle={() => setVettingModalOpen(false)} className="border-bottom">
          <span className="font-weight-bold">
            <i className="fas fa-sliders-h me-2 text-primary"></i> Manage Twilio & A2P 10DLC - {selectedVettingUser?.name}
          </span>
          <span className="text-muted ms-2 small">({selectedVettingUser?.email})</span>
        </ModalHeader>
        <ModalBody style={{ maxHeight: '75vh', overflowY: 'auto' }}>
          {selectedVettingUser ? (
            <div>
              {/* SECTION 1: TWILIO SUBACCOUNT & MESSAGING SERVICE */}
              <div className="p-3 mb-3 rounded border bg-light">
                <h6 className="font-weight-bold text-dark mb-3">
                  <i className="fas fa-server me-2 text-primary"></i> 1. Twilio Subaccount & Routing
                </h6>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label className="font-weight-bold text-muted small mb-1">Subaccount SID</Label>
                      <Input
                        type="text"
                        value={selectedVettingUser.twilioSubaccountSid || ""}
                        onChange={(e) => handleVettingFieldChange('topLevel', 'twilioSubaccountSid', e.target.value)}
                        placeholder="AC..."
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label className="font-weight-bold text-muted small mb-1">Subaccount Auth Token</Label>
                      <Input
                        type="password"
                        value={selectedVettingUser.twilioSubauthToken === "*******************" ? "*******************" : (selectedVettingUser.twilioSubauthToken || "")}
                        onChange={(e) => handleVettingFieldChange('topLevel', 'twilioSubauthToken', e.target.value)}
                        placeholder={selectedVettingUser.twilioSubauthToken ? "Masked token saved" : "Paste Subaccount Auth Token"}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <FormGroup className="mb-0">
                      <Label className="font-weight-bold text-muted small mb-1">Messaging Service SID</Label>
                      <Input
                        type="text"
                        value={selectedVettingUser.twilioMessagingServiceSid || ""}
                        onChange={(e) => handleVettingFieldChange('topLevel', 'twilioMessagingServiceSid', e.target.value)}
                        placeholder="MG..."
                      />
                    </FormGroup>
                  </Col>
                </Row>
              </div>

              {/* SECTION 2: A2P 10DLC SIDS & STATUS */}
              <div className="p-3 mb-3 rounded border bg-light">
                <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                  <h6 className="font-weight-bold text-dark mb-2 mb-md-0">
                    <i className="fas fa-shield-alt me-2 text-info"></i> 2. A2P 10DLC Carrier Registrations
                  </h6>
                  <Button
                    size="sm"
                    color="primary"
                    className="bg-primary font-weight-bold"
                    onClick={handleAdminSyncA2P}
                    disabled={syncingA2P || !selectedVettingUser.twilioBrandSid}
                  >
                    {syncingA2P ? (
                      <><Spinner size="sm" className="me-1" /> Checking Twilio...</>
                    ) : (
                      <><i className="fas fa-sync-alt me-1"></i> Sync Status from Twilio</>
                    )}
                  </Button>
                </div>

                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <Label className="font-weight-bold text-muted small mb-0">A2P Brand SID</Label>
                        {getA2PStatusBadge(selectedVettingUser.businessRegistrationStatus)}
                      </div>
                      <Input
                        type="text"
                        value={selectedVettingUser.twilioBrandSid || ""}
                        onChange={(e) => handleVettingFieldChange('topLevel', 'twilioBrandSid', e.target.value)}
                        placeholder="BN..."
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <Label className="font-weight-bold text-muted small mb-0">A2P Campaign SID</Label>
                        {getA2PStatusBadge(selectedVettingUser.campaignRegistrationStatus)}
                      </div>
                      <Input
                        type="text"
                        value={selectedVettingUser.twilioA2PCampaignSid || ""}
                        onChange={(e) => handleVettingFieldChange('topLevel', 'twilioA2PCampaignSid', e.target.value)}
                        placeholder="QE..."
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <FormGroup className="mb-0">
                      <Label className="font-weight-bold text-muted small mb-1">Brand Status Override</Label>
                      <Input
                        type="select"
                        value={selectedVettingUser.businessRegistrationStatus || "unsubmitted"}
                        onChange={(e) => handleVettingFieldChange('topLevel', 'businessRegistrationStatus', e.target.value)}
                      >
                        <option value="unsubmitted">Unsubmitted</option>
                        <option value="pending">Pending Review</option>
                        <option value="approved">Approved</option>
                        <option value="failed">Failed / Rejected</option>
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup className="mb-0">
                      <Label className="font-weight-bold text-muted small mb-1">Campaign Status Override</Label>
                      <Input
                        type="select"
                        value={selectedVettingUser.campaignRegistrationStatus || "unsubmitted"}
                        onChange={(e) => handleVettingFieldChange('topLevel', 'campaignRegistrationStatus', e.target.value)}
                      >
                        <option value="unsubmitted">Unsubmitted</option>
                        <option value="pending">Pending Review</option>
                        <option value="approved">Approved</option>
                        <option value="failed">Failed / Rejected</option>
                      </Input>
                    </FormGroup>
                  </Col>
                </Row>
              </div>

              {/* SECTION 3: COMPLIANCE GATE */}
              <div className="p-3 rounded border bg-light">
                <h6 className="font-weight-bold text-dark mb-2">
                  <i className="fas fa-lock me-2 text-warning"></i> 3. Compliance & Launch Gate
                </h6>
                <div className="p-2 bg-white rounded border">
                  <div className="d-flex align-items-center">
                    <input
                      type="checkbox"
                      id="editForceTestMessage"
                      checked={selectedVettingUser.forceTestMessage || false}
                      onChange={(e) => handleVettingFieldChange('topLevel', 'forceTestMessage', e.target.checked)}
                      style={{
                        width: "18px",
                        height: "18px",
                        cursor: "pointer",
                        marginRight: "10px",
                        accentColor: "#014c95"
                      }}
                    />
                    <label htmlFor="editForceTestMessage" className="mb-0 fw-bold" style={{ fontWeight: "700", cursor: "pointer", fontSize: "0.95rem", color: "#1e293b" }}>
                      Force Test Message Gate (Requires Admin Approval Before Campaign Launch)
                    </label>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-4">No user selected</div>
          )}
        </ModalBody>
        <ModalFooter className="d-flex justify-content-end border-top">
          <Button
            color="secondary"
            className="bg-secondary me-2 mr-2"
            onClick={() => setVettingModalOpen(false)}
            disabled={submittingVetting}
          >
            Close
          </Button>
          <Button
            color="primary"
            className="bg-primary font-weight-bold"
            onClick={handleSaveVettingDetails}
            disabled={submittingVetting}
          >
            {submittingVetting ? "Saving..." : "Save Configuration"}
          </Button>
        </ModalFooter>
      </Modal>
    </Container>
  );
};

export default Users;
